// Small formatting helpers. The server sends MySQL DATETIME strings ('YYYY-MM-DD HH:MM:SS').
import { useCallback, useEffect, useState } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// "10:31" today, "7 Oct 10:31" otherwise.
export function when(s) {
  if (!s) return '';
  const str = String(s).replace('T', ' ');
  const hm = str.slice(11, 16);
  if (str.slice(0, 10) === todayStr()) return hm;
  return `${Number(str.slice(8, 10))} ${MONTHS[Number(str.slice(5, 7)) - 1] || ''} ${hm}`.trim();
}

export const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// "8 Oct 2026" for a plain date ('2026-10-08', or '20261008' when compact is true); anything else comes back as it is.
export function day(v, compact = false) {
  const m = (compact ? /^(\d{4})-?(\d{2})-?(\d{2})$/ : /^(\d{4})-(\d{2})-(\d{2})$/).exec(String(v ?? '').trim());
  if (!m || !MONTHS[Number(m[2]) - 1] || Number(m[3]) < 1 || Number(m[3]) > 31) return v;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

// AI models in plain words. The stored setting is "smart" or "fast"; the server maps it to the AI service in use
// (DeepSeek, GLM…). Voice calls are counted as 'gemini-live'.
export const MODEL_LABELS = {
  'claude-sonnet-5-5': 'Smart',
  'claude-haiku-4-5': 'Fast and cheap',
  'claude-opus-5-5': 'Smartest',
  'gemini-live': 'Voice calls',
};
export const modelShort = (m) => (MODEL_LABELS[m] || m || '').replace(/ \(.*\)$/, '');

// The employee a history line or job belongs to. Someone who has left the office is no longer in agentsById,
// so their name (and look, when the server sends them) come from the line itself.
export function agentFor(agentsById, x) {
  if (agentsById[x.agentId]) return agentsById[x.agentId];
  if (!x.agentId) return null;
  const name = x.agentName || String(x.agentId).charAt(0).toUpperCase() + String(x.agentId).slice(1);
  return { id: x.agentId, name, look: x.agentLook || null, removed: true };
}

// Loads data for a screen: { data, error, loading, reload }.
export function useLoad(fn, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(fn, deps);
  const reload = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    return load().then(
      (data) => setState({ data, error: null, loading: false }),
      (error) => setState({ data: null, error, loading: false })
    );
  }, [load]);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}
